import {
	type ExternalFormData,
	type Form360Data,
	STATIC_EMPLOYEE_OPTIONS,
	type StandardFormData,
} from "../../../../../../../components/FormSelectionCard";

import type { ConfiguredForm, FormsByType } from "../types";

import type { TemplateFormAssignmentWithNames } from "../../../../../../../api/services/defs/review-templates";

type WithNames = TemplateFormAssignmentWithNames;
type InternalAssignment = Extract<WithNames, { basic: true }>;
type ExternalAssignment = Extract<WithNames, { external: true }>;
type EasyAddAssignment = Extract<WithNames, { easyAdd: true }>;

const generateLocalId = () => crypto.randomUUID();

// Stored labels for relational signers vary in casing (e.g. "Employee's manager"); display the canonical labels
const STATIC_SIGNER_NAME_BY_ID = new Map(STATIC_EMPLOYEE_OPTIONS.map((option) => [option.value.id, option.value.name]));

export const WHO_CAN_ADD_FORM: Record<NonNullable<EasyAddAssignment["whoCanEasyAdd"]>, Form360Data["whoCanAddForm"]> = {
	"any-employee": "Any Employee",
	subject: "Subject",
	"manager-chain-of-command": "Manager",
	admin: "Admin",
};

export const APPROVAL_LABEL: Record<Exclude<EasyAddAssignment["requiresApproval"], null>, Form360Data["approval"]> = {
	"managers-and-admins": "Require Approval from Admins or Managers",
	"admins-only": "Require Approval from Admins",
};

type EasyAddFields = {
	formName?: string;
	whoCanEasyAdd?: EasyAddAssignment["whoCanEasyAdd"];
	subjectEmployeeChooseAuthor?: EasyAddAssignment["subjectEmployeeChooseAuthor"];
	requiresApproval: EasyAddAssignment["requiresApproval"];
	signers?: Array<{ id: string | { toString(): string }; name: string }>;
	visible: boolean;
	redacted: boolean;
	allowComments?: boolean;
	isActive?: boolean;
};

/**
 * Map an easy-add config's API fields into the 360 card's form state, minus the
 * date fields — callers own those (templates are relative-only, cycle configs
 * are static or relative). Shared by the wizard and the Edit 360 Settings page
 * so the enum labels and the subject-author branch can't drift.
 */
export const easyAddFieldsToForm360Data = (
	a: EasyAddFields
): Omit<Form360Data, "authorsDueDate" | "signersDueDate" | "isRelativeDates" | "dueDatesToggle"> => {
	const whoCanAddForm = a.whoCanEasyAdd ? WHO_CAN_ADD_FORM[a.whoCanEasyAdd] : "Any Employee";

	return {
		formType: "360",
		formName: a.formName ?? "",
		whoCanAddForm,
		formAuthor:
			whoCanAddForm === "Subject"
				? a.subjectEmployeeChooseAuthor === "any-employee"
					? "Any Employee"
					: "Subject"
				: undefined,
		approval: a.requiresApproval ? APPROVAL_LABEL[a.requiresApproval] : "No Approval Needed",
		formSigners: (a.signers ?? []).map((p) => {
			const id = String(p.id);
			return { id, name: STATIC_SIGNER_NAME_BY_ID.get(id) ?? p.name };
		}),
		makeVisibleWhenFinished: a.visible,
		allow360RequestMessage: a.allowComments ?? false,
		redactAuthors: a.redacted,
		makeFormInactive: a.isActive === false,
	};
};

const transformInternal = (a: InternalAssignment): ConfiguredForm<"standard"> => {
	const data: StandardFormData = {
		formType: "standard",
		formName: a.formName ?? "",
		formAuthors: a.authors.map((p) => ({ id: String(p.id), name: p.name })),
		formSigners: (a.signers ?? []).map((p) => ({ id: String(p.id), name: p.name })),
		authorsDueDate: a.daysToCompleteAuthors,
		signersDueDate: a.daysToCompleteSigners,
		isRelativeDates: true,
		makeVisibleWhenCompleted: a.visible,
		redactAuthors: a.redacted,
	};

	return {
		localId: generateLocalId(),
		formId: String(a.formBaseId),
		assignmentId: String(a.id),
		fromTemplate: true,
		data,
	};
};

const transformExternal = (a: ExternalAssignment): ConfiguredForm<"external"> => {
	const firstAuthor = a.authors?.[0];
	const data: ExternalFormData = {
		formType: "external",
		formName: a.formName ?? "",
		reviewerName: firstAuthor?.name || undefined,
		reviewerEmail: firstAuthor?.email || undefined,
		emailMessage: firstAuthor?.emailBody || undefined,
		makeVisibleWhenFinished: a.visible,
		redactAuthors: a.redacted,
	};

	return {
		localId: generateLocalId(),
		formId: String(a.formBaseId),
		assignmentId: String(a.id),
		fromTemplate: true,
		data,
	};
};

const transformEasyAdd = (a: EasyAddAssignment): ConfiguredForm<"360"> => {
	const data: Form360Data = {
		...easyAddFieldsToForm360Data(a),
		authorsDueDate: a.daysToCompleteAuthors,
		// Easy-add signer days are stored as total days from form-added; the card's field is days
		// from the authors due date, so convert to the delta (legacy EasyAddFormCard does the same).
		signersDueDate:
			a.daysToCompleteSigners != null && a.daysToCompleteAuthors != null
				? Math.max(0, a.daysToCompleteSigners - a.daysToCompleteAuthors)
				: a.daysToCompleteSigners,
		isRelativeDates: true,
		dueDatesToggle: false,
	};

	return {
		localId: generateLocalId(),
		formId: String(a.formBaseId),
		assignmentId: String(a.id),
		fromTemplate: true,
		data,
	};
};

const isInternal = (a: WithNames): a is InternalAssignment => "basic" in a && a.basic === true;
const isExternal = (a: WithNames): a is ExternalAssignment => "external" in a && a.external === true;
const isEasyAdd = (a: WithNames): a is EasyAddAssignment => "easyAdd" in a && a.easyAdd === true;

/**
 * Map the v2 detail-endpoint shape (`TemplateFormAssignmentWithNames[]`) into our ConfiguredForm
 * buckets. Inactive 360 forms are filtered out unless `includeInactive360` is set — a cycle must
 * not instantiate them, but the template editor has to round-trip them or saving would delete them.
 */
export const templateAssignmentsToConfiguredForms = (
	assignments: WithNames[],
	options: { includeInactive360?: boolean } = {}
): FormsByType => {
	const buckets: FormsByType = { standard: [], "360": [], external: [] };

	for (const a of assignments) {
		if (isExternal(a)) {
			buckets.external.push(transformExternal(a));
		} else if (isEasyAdd(a)) {
			if (options.includeInactive360 || a.isActive !== false) buckets["360"].push(transformEasyAdd(a));
		} else if (isInternal(a)) {
			buckets.standard.push(transformInternal(a));
		}
	}

	return buckets;
};

export { generateLocalId };
